import { useCallback, useEffect, useState } from "react";
import { apiFetch } from "../../apiClient";
import { useAuth } from "../../context/AuthContext";
import { AuditDashboardStats, AuditTask } from "../../types";
import AdminAuditPanel from "./AdminAuditPanel";
import UserAuditPanel from "./UserAuditPanel";

interface DbUser {
  id: number;
  username: string;
  role: string;
  is_active?: boolean;
}

export default function AuditTaskView() {
  const { user, isAdmin, isAuthenticated } = useAuth();
  const [tasks, setTasks] = useState<AuditTask[]>([]);
  const [users, setUsers] = useState<DbUser[]>([]);
  const [stats, setStats] = useState<AuditDashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const jsonHeaders = () => ({ "Content-Type": "application/json" });

  const loadTasks = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    setError("");
    try {
      const res = await apiFetch("audit-tasks/");
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.detail || "Errore caricamento task");
      setTasks(body.tasks || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Errore imprevisto");
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const loadStats = useCallback(async () => {
    if (!isAdmin || !isAuthenticated) return;
    setStatsLoading(true);
    try {
      const res = await apiFetch("audit-tasks/dashboard");
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.detail || "Errore dashboard");
      setStats(body);
    } catch (err) {
      console.error(err);
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  }, [isAdmin, isAuthenticated]);

  const loadUsers = useCallback(async () => {
    if (!isAdmin || !isAuthenticated) return;
    try {
      const res = await apiFetch("users/");
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.detail || "Errore utenti");
      setUsers(body.users || []);
    } catch (err) {
      console.error(err);
    }
  }, [isAdmin, isAuthenticated]);

  useEffect(() => {
    loadTasks();
    loadUsers();
    loadStats();
  }, [loadTasks, loadUsers, loadStats]);

  const handleCreateTask = async (payload: {
    assignee_user_ids: number[];
    target_count: number;
    due_date: string;
    notes?: string;
  }) => {
    setSubmitting(true);
    setError("");
    try {
      const res = await apiFetch("audit-tasks/", {
        method: "POST",
        headers: jsonHeaders(),
        body: JSON.stringify(payload),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.detail || "Errore creazione task");
      await loadTasks();
      await loadStats();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Errore imprevisto");
      throw err;
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelTask = async (taskId: number) => {
    try {
      const res = await apiFetch(`audit-tasks/${taskId}`, {
        method: "PATCH",
        headers: jsonHeaders(),
        body: JSON.stringify({ status: "cancelled" }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.detail || "Errore annullamento");
      await loadTasks();
      await loadStats();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Errore imprevisto");
    }
  };

  if (!user) {
    return <p className="text-sm text-gray-500">Accedi per vedere le task.</p>;
  }

  return isAdmin ? (
    <AdminAuditPanel
      tasks={tasks}
      users={users}
      stats={stats}
      loading={loading}
      statsLoading={statsLoading}
      submitting={submitting}
      error={error}
      onCreateTask={handleCreateTask}
      onCancelTask={handleCancelTask}
    />
  ) : (
    <UserAuditPanel tasks={tasks} loading={loading} />
  );
}
